import { ChildGroupColumn, ChildGroupColumnKeys, ChildGroupReadRecordsParams } from '../types';
import { GridBaseDataProvider } from '../../../../../../grid/GridBaseDataProvider';
import {
  GetRowsRequest,
  GetSummaryRequest,
  SetFiltersRequest,
  UpdateSettingsRequest,
  UpdateSettingsResponse,
} from '../../../../../../grid/types/common';
import { ConsoleRequest, DataProviderType } from '../../../../../../utils/request';
import { convertGridFiltersToExtJsFilters } from '../../../../../../grid/utils/filters';
import { ExtJsFilter } from '../../../../../../grid/utils/filters/types';
import { AssignPolicyHelper } from '../../helpers/assign.policy.helper';

export class ChildGroupsGridDataProvider extends GridBaseDataProvider<ChildGroupColumn, ChildGroupColumnKeys> {
  private getCountPromise: Promise<number> | undefined;
  private countPromiseFunctions: {
    resolve: Function;
    reject: Function;
  };

  private columnKeyToSortableField: Map<ChildGroupColumnKeys, string> = new Map([
    [ChildGroupColumnKeys.POLICY, 'assignedPolicy.policyName'],
  ]);

  private gridFilters: Array<ExtJsFilter<ChildGroupColumnKeys>>;

  //@ts-ignore
  async getSummary(request: GetSummaryRequest<ChildGroupColumnKeys>) {
    this.setupCountPromise();
    const numItems = await this.getCountPromise;
    delete this.getCountPromise;

    return {
      summary: '',
      numItems: numItems,
    };
  }

  async getRows(request: GetRowsRequest<ChildGroupColumn, ChildGroupColumnKeys>): Promise<any> {
    const response = await this.readRecords(request);

    return {
      success: response.success,
      rawEvents: response.rawEvents,
    };
  }

  async setFilters(request: SetFiltersRequest<ChildGroupColumnKeys>): Promise<{} | undefined> {
    const policyFilter = request.filters.find(filter => filter[ChildGroupColumnKeys.POLICY]);
    if (!policyFilter) {
      console.error('Could not find policy filter.');
      return;
    }

    const policyFilterValue = policyFilter[ChildGroupColumnKeys.POLICY];

    let filters = request.filters.filter(filter => !filter[ChildGroupColumnKeys.POLICY]);
    if (policyFilterValue && policyFilterValue[Object.keys(policyFilterValue)[0]]) {
      filters = request.filters.map(filter => {
        if (filter[ChildGroupColumnKeys.POLICY]) {
          return {
            [ChildGroupColumnKeys.POLICY_ID]: filter[ChildGroupColumnKeys.POLICY],
          };
        }

        return filter;
      });
    }

    this.gridFilters = convertGridFiltersToExtJsFilters(filters);
    return;
  }

  private async readRecords(request: Partial<GetRowsRequest<ChildGroupColumn, ChildGroupColumnKeys>>): Promise<any> {
    if (!request.pageSize || !request.pageNumber) {
      throw Error('No page size sent for grid request!');
    }

    const payload: ChildGroupReadRecordsParams = {
      page: request.pageNumber,
      limit: request.pageSize,
      additionalFilters: {
        type: [
          Constants.Network.Filters.Types.COMPANIES,
          Constants.Network.Filters.Types.COMPUTERS,
          Constants.Network.Filters.Types.VIRTUAL_MACHINES,
        ],
        policyTemplate: '',
        depth: Constants.ProtectedEntities.EPS.FILTERS.DEPTH.SHOW_ALL_ITEMS_RECURSIVELY,
        tagAttribute: '',
        tagValue: '',
        tagStore: [],
      },
      protectedEntitiesViewTypes: AssignPolicyHelper.getProtectedEntitiesViewTypes(this.dependencies.targets),
    };

    if (request.sortColumnName && request.sortDirection) {
      payload.sort = this.columnKeyToSortableField.has(<ChildGroupColumnKeys>request.sortColumnName)
        ? this.columnKeyToSortableField.get(<ChildGroupColumnKeys>request.sortColumnName)
        : request.sortColumnName;
      payload.dir = request.sortDirection;
    }

    if (this.gridFilters && this.gridFilters.length !== 0) {
      payload.filter = this.gridFilters;
    }

    const response = await ConsoleRequest.createRequest<any>({
      dataProviderType: DataProviderType.EPS,
      endpoint: 'ProtectedEntitiesEPS',
      method: 'getChildrenProtectedEntitiesWithDirectlyAssignedPolicies',
      payload: payload,
    });

    this.countPromiseFunctions.resolve(response.total);

    return {
      success: response.success,
      rawEvents: response.data,
    };
  }

  private setupCountPromise(): void {
    if (!this.getCountPromise) {
      this.getCountPromise = new Promise((resolve, reject) => {
        this.countPromiseFunctions = { resolve, reject };
      });
    }
  }

  // we do nothing when settings are updated
  async updateSettings(params: UpdateSettingsRequest<ChildGroupColumn>): Promise<UpdateSettingsResponse> {
    return {};
  }
}
